import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody, EmptyState, StatTile } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { ServerCog, ShieldCheck, ShieldAlert, Download } from "lucide-react";

const STATE_LABEL = {
  NOT_CONNECTED: "NOT CONNECTED",
  SANDBOX: "SANDBOX",
  SIMULATOR: "SIMULATOR",
  LIVE: "LIVE",
};
const STATE_CLS = {
  NOT_CONNECTED: "dt6-chip",
  SANDBOX: "dt6-chip dt6-chip-amber",
  SIMULATOR: "dt6-chip dt6-chip-amber",
  LIVE: "dt6-chip dt6-chip-success",
};

export function AdminIntegrations() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creds, setCreds] = useState({});
  const [newState, setNewState] = useState("SANDBOX");
  const [err, setErr] = useState("");
  const [health, setHealth] = useState({});
  const load = () => api.get("/admin/integrations").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditing(row);
    setNewState(row.state);
    const c = {};
    (row.expected_credentials || []).forEach((k) => { c[k] = ""; });
    setCreds(c);
    setErr("");
  };

  const save = async () => {
    setErr("");
    try {
      const payload = { state: newState };
      const nonempty = Object.fromEntries(Object.entries(creds).filter(([, v]) => v));
      if (Object.keys(nonempty).length > 0) payload.credentials = nonempty;
      await api.put(`/admin/integrations/${editing.module_code}`, payload);
      setEditing(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const runHealth = async (row) => {
    try {
      const { data } = await api.post(`/admin/integrations/${row.module_code}/health`);
      setHealth((h) => ({ ...h, [row.module_code]: data }));
    } catch (e) { setHealth((h) => ({ ...h, [row.module_code]: { error: formatApiError(e) } })); }
  };

  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Integrations" testId="admin-integrations-header" />
      <PortalBody>
        <div className="mb-4 text-xs font-mono tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">
          / ADAPTER FRAMEWORK · NOT_CONNECTED → SANDBOX → LIVE · LIVE REQUIRES ENCRYPTED CREDENTIALS
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => (
            <div key={r.module_code} className="dt6-card p-5" data-testid={`integration-card-${r.module_code}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-mono text-[0.7rem] tracking-[0.18em] text-[color:var(--dt6-muted)]">{r.module_code} · {r.adapter}</div>
                  <div className="font-heading text-lg font-bold mt-1">{r.vendor_class}</div>
                </div>
                <span className={STATE_CLS[r.state]} data-testid={`integration-state-${r.module_code}`}>{STATE_LABEL[r.state]}</span>
              </div>
              <div className="text-xs text-[color:var(--dt6-muted)]">
                {r.credentials_present ? (
                  <div className="space-y-1" data-testid={`integration-creds-${r.module_code}`}>
                    {Object.entries(r.credentials_preview || {}).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between">
                        <span className="font-mono">{k}</span><span className="font-mono">{v}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>NO CREDENTIALS STORED{r.expected_credentials?.length ? `. NEEDS: ${r.expected_credentials.join(", ")}` : "."}</div>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <button className="dt6-btn text-xs flex-1 justify-center" onClick={() => openEdit(r)} data-testid={`integration-edit-${r.module_code}`}>
                  <ServerCog size={12} /> Configure
                </button>
                <button className="dt6-btn text-xs" onClick={() => runHealth(r)} data-testid={`integration-health-${r.module_code}`} title="Run health check">
                  {r.state === "LIVE" ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />}
                </button>
              </div>
              {health[r.module_code] && (
                <div className="mt-2 text-[0.65rem] font-mono text-[color:var(--dt6-muted)] break-all" data-testid={`integration-health-result-${r.module_code}`}>
                  {JSON.stringify(health[r.module_code])}
                </div>
              )}
            </div>
          ))}
        </div>

        {editing && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
            <div className="dt6-card p-6 w-full max-w-xl" style={{ background: "var(--dt6-surface)" }} onClick={(e) => e.stopPropagation()} data-testid="integration-modal">
              <div className="dt6-chip mb-3">/ CONFIGURE {editing.module_code}</div>
              <h3 className="font-heading text-2xl font-bold">{editing.adapter}</h3>
              <p className="text-sm text-[color:var(--dt6-muted)]">Vendor class: {editing.vendor_class}</p>
              <div className="mt-4">
                <label className="dt6-label">State</label>
                <select className="dt6-select" value={newState} onChange={(e) => setNewState(e.target.value)} data-testid="integration-state-select">
                  <option value="NOT_CONNECTED">NOT_CONNECTED — route to concierge queue</option>
                  <option value="SANDBOX">SANDBOX — hit vendor sandbox, label deliverables TEST</option>
                  <option value="SIMULATOR">SIMULATOR — deterministic synthetic output (test accounts only)</option>
                  <option value="LIVE">LIVE — production traffic</option>
                </select>
              </div>
              {(editing.expected_credentials || []).length > 0 && (
                <div className="mt-4 space-y-3">
                  <div className="text-xs font-mono tracking-[0.14em] uppercase text-[color:var(--dt6-muted)]">
                    / CREDENTIALS · ENCRYPTED SERVER-SIDE · MASKED IN UI · NEVER LOGGED
                  </div>
                  {(editing.expected_credentials || []).map((k) => (
                    <div key={k}>
                      <label className="dt6-label">{k}</label>
                      <input
                        type="password"
                        className="dt6-input"
                        placeholder={editing.credentials_present ? "(leave blank to keep existing)" : ""}
                        value={creds[k] || ""}
                        onChange={(e) => setCreds({ ...creds, [k]: e.target.value })}
                        data-testid={`integration-cred-${k}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              {err && <div className="mt-3 text-sm text-[color:var(--dt6-danger)]" data-testid="integration-error">{err}</div>}
              <div className="mt-6 flex justify-end gap-2">
                <button className="dt6-btn text-xs" onClick={() => setEditing(null)} data-testid="integration-cancel">Cancel</button>
                <button className="dt6-btn dt6-btn-primary text-xs" onClick={save} data-testid="integration-save">Save</button>
              </div>
            </div>
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminAdjudication() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/adjudication").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (adj_id, decision) => {
    await api.post(`/admin/adjudication/${adj_id}`, { decision });
    await load();
  };
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Adjudication queue" testId="admin-adjudication-header" />
      <PortalBody>
        {rows.length === 0 ? (
          <EmptyState testId="adjudication-empty">ADJUDICATION QUEUE IS CLEAR.</EmptyState>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="dt6-card p-5" data-testid={`adj-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="dt6-chip dt6-chip-amber mb-2">{r.reason?.toUpperCase()}</div>
                    <div className="font-heading font-bold">
                      {r.lead?.lead_type} · {r.lead?.contact?.email || r.lead?.contact?.name || "unknown"}
                    </div>
                    <div className="text-xs text-[color:var(--dt6-muted)]">flagged {new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <button className="dt6-btn text-xs" onClick={() => decide(r.id, "reject")} data-testid={`adj-reject-${r.id}`}>Reject</button>
                    <button className="dt6-btn dt6-btn-primary text-xs" onClick={() => decide(r.id, "release")} data-testid={`adj-release-${r.id}`}>Release</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PortalBody>
    </>
  );
}

export function AdminReports() {
  const [tab, setTab] = useState("mrr");
  return (
    <>
      <PortalHeader eyebrow="/ ADMIN" title="Reports" testId="admin-reports-header">
        <div className="flex gap-1">
          {["mrr","exchange","pixel","partners"].map((t) => (
            <button key={t} className={`dt6-btn text-xs ${tab===t?"dt6-btn-primary":""}`} onClick={() => setTab(t)} data-testid={`report-tab-${t}`}>{t}</button>
          ))}
        </div>
      </PortalHeader>
      <PortalBody>
        {tab === "mrr" && <MrrTab />}
        {tab === "exchange" && <ExchangeTab />}
        {tab === "pixel" && <PixelTab />}
        {tab === "partners" && <PartnersTab />}
      </PortalBody>
    </>
  );
}

function MrrTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/mrr").then((r) => setData(r.data)); }, []);
  if (!data) return <EmptyState testId="report-loading">LOADING...</EmptyState>;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatTile label="MRR (total)" value={`$${(data.total_mrr || 0).toLocaleString()}`} testId="report-mrr-total" />
      </div>
      {data.rows.length === 0 ? (
        <EmptyState testId="report-mrr-empty">NO ACTIVE SUBSCRIPTIONS.</EmptyState>
      ) : (
        <div className="dt6-card overflow-hidden">
          <table className="dt6-table">
            <thead><tr><th>Tier</th><th>Subscribers</th><th>MRR</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.tier} data-testid={`report-mrr-${r.tier}`}>
                  <td className="font-mono uppercase">{r.tier}</td>
                  <td className="font-mono">{r.subscribers}</td>
                  <td className="font-mono">${r.mrr.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ExchangeTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports/exchange").then((r) => setData(r.data)); }, []);
  if (!data) return <EmptyState testId="report-loading">LOADING...</EmptyState>;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <StatTile label="Total routed" value={data.total_routed} testId="report-exchange-routed" />
        <StatTile label="Revenue" value={`$${(data.total_revenue || 0).toFixed(2)}`} testId="report-exchange-revenue" />
      </div>
      <div className="dt6-card p-4">
        <div className="dt6-chip mb-3">/ BY LEAD TYPE</div>
        {(data.by_type || []).length === 0 ? (
          <EmptyState testId="report-exchange-empty">NO LEADS YET.</EmptyState>
        ) : (
          <table className="dt6-table">
            <thead><tr><th>Type</th><th>Count</th></tr></thead>
            <tbody>{data.by_type.map((r) => (<tr key={r.lead_type}><td className="font-mono">{r.lead_type}</td><td className="font-mono">{r.count}</td></tr>))}</tbody>
          </table>
        )}
      </div>
    </>
  );
}

function PixelTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/reports/pixel").then((r) => setRows(r.data)); }, []);
  if (rows.length === 0) return <EmptyState testId="report-pixel-empty">NO PIXEL EVENTS YET.</EmptyState>;
  return (
    <div className="dt6-card overflow-hidden">
      <table className="dt6-table">
        <thead><tr><th>Site</th><th>Domain</th><th>Events</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.site_key} data-testid={`report-pixel-row-${r.site_key}`}>
            <td>{r.site?.name || r.site_key.slice(0,12)}</td>
            <td className="text-xs">{r.site?.domain || "—"}</td>
            <td className="font-mono">{r.events}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function PartnersTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/reports/partner-leaderboard").then((r) => setRows(r.data)); }, []);
  if (rows.length === 0) return <EmptyState testId="report-partners-empty">NO PARTNER-SUBMITTED ORDERS YET.</EmptyState>;
  return (
    <div className="dt6-card overflow-hidden">
      <table className="dt6-table">
        <thead><tr><th>Partner</th><th>Org</th><th>Orders</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.partner_id} data-testid={`report-partner-${r.partner_id}`}>
            <td>{r.partner_name || "—"}</td>
            <td className="text-xs text-[color:var(--dt6-muted)]">{r.partner_org || "—"}</td>
            <td className="font-mono">{r.orders}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// Small helper: add CSV export dropdown to any admin page — used in the Metrics header
export function CsvExportButton({ collection, label = "Export CSV", testId }) {
  const api_url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/admin/export/" + collection;
  const trigger = () => {
    // Use fetch to include credentials, then trigger download
    fetch(api_url, { credentials: "include" }).then(async (r) => {
      if (!r.ok) { alert("Export failed."); return; }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `dt6-${collection}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    });
  };
  return (
    <button className="dt6-btn text-xs" onClick={trigger} data-testid={testId || `export-${collection}`}>
      <Download size={12} /> {label}
    </button>
  );
}
